import React from 'react';
import { CloseIcon, InfoOutlinedIcon } from '@/components/icons';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { useWidgetI18n } from '../i18n/WidgetI18nProvider';

export const VOICE_PRIVACY_ACK_KEY = 'engage:widget:voice_privacy_ack';

/**
 * Returns whether the user has acknowledged the voice privacy disclosure
 * in the current browser session. SSR-safe and tolerates browsers where
 * sessionStorage is unavailable (private mode, blocked storage, etc.).
 *
 * Per AC the disclosure is "one-time per-session" — closing the tab /
 * browser resets the acknowledgement so the next session shows it again.
 */
export function hasAcknowledgedVoicePrivacy(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(VOICE_PRIVACY_ACK_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Persists the privacy acknowledgement for the current browser session
 * (sessionStorage; cleared when the tab closes). Failures are intentionally
 * swallowed - the worst case is the user sees the banner one more time.
 */
export function acknowledgeVoicePrivacy(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(VOICE_PRIVACY_ACK_KEY, '1');
  } catch {
    // sessionStorage may be blocked / disabled — fail silently.
  }
}

interface VoicePrivacyDisclosureProps {
  onDismiss: () => void;
}

/**
 * Lightweight privacy notice rendered above the chat input the first time
 * a visitor taps the microphone in a browser session. Copy is verbatim
 * from the AC and is the only place we surface that voice notes are sent
 * to a transcription model on the server. The user dismisses it via the
 * close button or by sending any message; we persist that fact in
 * sessionStorage so it doesn't reappear within the same session, and it
 * resets naturally for the next session.
 */
export function VoicePrivacyDisclosure({ onDismiss }: VoicePrivacyDisclosureProps) {
  const theme = useTheme();
  const { t, locale } = useWidgetI18n();

  return (
    <Box
      role="status"
      aria-live="polite"
      data-testid="voice-privacy-disclosure"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        px: 1.5,
        py: 1,
        mb: 1,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[1],
      }}
    >
      <InfoOutlinedIcon
        sx={{ fontSize: 18, color: 'text.secondary', mt: '2px', flexShrink: 0 }}
      />
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            Voice will be transcribed by AI
          </Box>{' '}
          <Box component="span" sx={{ color: 'text.secondary' }}>
            Transcription supports multilingual support
          </Box>
        </Typography>
      </Box>
      <IconButton
        size="small"
        lang={locale}
        aria-label={t('voice.dismissPrivacyDisclosure')}
        onClick={() => {
          acknowledgeVoicePrivacy();
          onDismiss();
        }}
        sx={{ p: 0.5, color: 'text.secondary' }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}
